import { Head } from "vite-react-ssg";
import { useTranslation } from "react-i18next";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noindex?: boolean;
}

const SITE_URL = "https://www.dyna-agency.com";

const SEO = ({ title, description, path = "", image = "/og-image.png", noindex = false }: SEOProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language === "he" ? "he" : "en";
  const dir = lang === "he" ? "rtl" : "ltr";
  const url = `${SITE_URL}${path}`;
  const imageUrl = image.startsWith("/") ? `${SITE_URL}${image}` : image;

  const segments = path.split("/").filter(Boolean);
  const breadcrumbJsonLd =
    segments.length > 0
      ? JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            ...segments.map((seg, i) => ({
              "@type": "ListItem",
              position: i + 2,
              name: seg
                .split("-")
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" "),
              item: `${SITE_URL}/${segments.slice(0, i + 1).join("/")}`,
            })),
          ],
        })
      : null;

  return (
    <Head>
      <html lang={lang} dir={dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="he" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={lang === "he" ? "he_IL" : "en_US"} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {breadcrumbJsonLd && (
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
      )}
    </Head>
  );
};

export default SEO;
